/**
 * Centralized Compliance Constants
 * All regulatory time periods, limits, and thresholds in one place
 * Based on UK Workboat Code Edition 3 and MGN 505
 */

export const COMPLIANCE_CONSTANTS = {
  // Hours of Rest Requirements (MGN 505)
  HOURS_OF_REST: {
    MIN_REST_24H: 10,              // Minimum 10 hours rest in any 24-hour period
    MIN_REST_7_DAYS: 77,           // Minimum 77 hours rest in any 7-day period
    MAX_WORK_24H: 14,              // Maximum 14 hours work in any 24-hour period
    MAX_WORK_7_DAYS: 72,           // Maximum 72 hours work in any 7-day period
    GRACE_PERIOD_DAYS: 1,          // Compliance checked on 1st of following month
    DEFAULT_REST_HOURS: 24,        // Default rest hours for empty days
  },

  // Familiarisation Requirements
  FAMILIARISATION: {
    COMPLETION_HOURS: 24,          // Must be completed within 24 hours of joining
    WARNING_HOURS: 12,             // Warning threshold (50% of completion time)
  },

  // Certificate Expiry Warnings
  CERTIFICATES: {
    EXPIRY_WARNING_DAYS: 30,       // Warning when certificate expires within 30 days
    EXPIRY_CRITICAL_DAYS: 7,       // Critical warning within 7 days
  },

  // Equipment Check Frequencies
  EQUIPMENT_CHECKS: {
    WEEKLY_DAYS: 7,                // Weekly check frequency
    MONTHLY_DAYS: 30,              // Monthly check frequency
    QUARTERLY_DAYS: 90,            // Quarterly check frequency
    ANNUAL_DAYS: 365,              // Annual check frequency
    WARNING_THRESHOLD_PERCENT: 90,  // Warning when 90% of interval has elapsed
  },

  // Drill Requirements
  DRILLS: {
    FIRE_DRILL_DAYS: 30,           // Fire drill every 30 days (MANDATORY - SI 1999/2722 Reg 8(2))
    ABANDON_SHIP_DAYS: 30,         // Abandon ship drill every 30 days (MANDATORY - SI 1999/2722 Reg 8(2))
    MAN_OVERBOARD_DAYS: 90,        // Man overboard drill every 90 days (WBC3 14.11.2 - "regularly routinely")
    SECURITY_DRILL_DAYS: 90,       // Security drill every 90 days
    WARNING_THRESHOLD_DAYS: 7,      // Warning when drill due within 7 days
    OVERDUE_THRESHOLD_DAYS: 0,     // Overdue when past due date
  },

  // Training and Certification
  TRAINING: {
    ONBOARD_TRAINING_DAYS: 90,     // Complete onboard training within 90 days
    REFRESHER_TRAINING_YEARS: 5,   // Refresher training every 5 years
    MEDICAL_CERTIFICATE_YEARS: 2,  // Medical certificate valid for 2 years
  },

  // Document Review Periods
  DOCUMENT_REVIEW: {
    POLICY_REVIEW_DAYS: 365,       // Annual policy review
    PROCEDURE_REVIEW_DAYS: 365,    // Annual procedure review
    RISK_ASSESSMENT_DAYS: 365,     // Annual risk assessment review
    CRITICAL_REVIEW_DAYS: 180,     // 6-month review for critical documents
  },

  // Voyage and Operations
  VOYAGE: {
    PRE_DEPARTURE_HOURS: 24,       // Pre-departure checks within 24 hours
    PASSAGE_PLAN_HOURS: 12,        // Passage plan completed 12 hours before
    WEATHER_WINDOW_HOURS: 6,       // Weather check within 6 hours of departure
  },

  // Defect Management
  DEFECTS: {
    CRITICAL_RESOLUTION_HOURS: 24, // Critical defects must be resolved within 24 hours
    HIGH_RESOLUTION_DAYS: 7,       // High priority within 7 days
    MEDIUM_RESOLUTION_DAYS: 30,    // Medium priority within 30 days
    LOW_RESOLUTION_DAYS: 90,       // Low priority within 90 days
  },

  // Dashboard and UI
  DASHBOARD: {
    RECENT_ACTIVITY_DAYS: 7,       // Show activities from last 7 days
    UPCOMING_ITEMS_DAYS: 30,       // Show upcoming items for next 30 days
    OVERDUE_DISPLAY_DAYS: 90,      // Show overdue items up to 90 days old
  },

  // Compliance Scoring
  COMPLIANCE_SCORE: {
    EXCELLENT_THRESHOLD: 95,       // 95%+ compliance score
    GOOD_THRESHOLD: 85,            // 85-94% compliance score
    ADEQUATE_THRESHOLD: 75,        // 75-84% compliance score
    POOR_THRESHOLD: 60,            // 60-74% compliance score
    // Below 60% is considered critical
  },

  // Time Formatting
  TIME_UNITS: {
    HOURS_PER_DAY: 24,
    DAYS_PER_WEEK: 7,
    DAYS_PER_MONTH: 30,            // Average for calculations
    DAYS_PER_YEAR: 365,
    MONTHS_PER_YEAR: 12,
  }
};

// Helper functions for common calculations
export const ComplianceHelpers = {
  // Check if a date is within warning period
  isWithinWarningPeriod: (dueDate, warningDays = COMPLIANCE_CONSTANTS.CERTIFICATES.EXPIRY_WARNING_DAYS) => {
    const now = new Date();
    const due = new Date(dueDate);
    const daysUntilDue = Math.floor((due - now) / (1000 * 60 * 60 * 24));
    return daysUntilDue <= warningDays && daysUntilDue > 0;
  },

  // Check if a date is overdue
  isOverdue: (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    return due < now;
  },

  // Calculate compliance percentage
  calculateCompliancePercentage: (compliantItems, totalItems) => {
    if (totalItems === 0) return 100;
    return Math.round((compliantItems / totalItems) * 100);
  },

  // Get compliance status from percentage
  getComplianceStatus: (percentage) => {
    const { COMPLIANCE_SCORE } = COMPLIANCE_CONSTANTS;
    if (percentage >= COMPLIANCE_SCORE.EXCELLENT_THRESHOLD) return 'excellent';
    if (percentage >= COMPLIANCE_SCORE.GOOD_THRESHOLD) return 'good';
    if (percentage >= COMPLIANCE_SCORE.ADEQUATE_THRESHOLD) return 'adequate';
    if (percentage >= COMPLIANCE_SCORE.POOR_THRESHOLD) return 'poor';
    return 'critical';
  },

  // Format time period for display
  formatTimePeriod: (days) => {
    const { TIME_UNITS } = COMPLIANCE_CONSTANTS;
    if (days === 1) return '24 hours';
    if (days < TIME_UNITS.DAYS_PER_WEEK) return `${days} days`;
    if (days === TIME_UNITS.DAYS_PER_WEEK) return '1 week';
    if (days < TIME_UNITS.DAYS_PER_MONTH) {
      const weeks = Math.floor(days / TIME_UNITS.DAYS_PER_WEEK);
      return `${weeks} week${weeks > 1 ? 's' : ''}`;
    }
    if (days === TIME_UNITS.DAYS_PER_MONTH) return '1 month';
    if (days < TIME_UNITS.DAYS_PER_YEAR) {
      const months = Math.floor(days / TIME_UNITS.DAYS_PER_MONTH);
      return `${months} month${months > 1 ? 's' : ''}`;
    }
    const years = Math.floor(days / TIME_UNITS.DAYS_PER_YEAR);
    return `${years} year${years > 1 ? 's' : ''}`;
  }
};

export default COMPLIANCE_CONSTANTS;